import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { getApiErrorReason } from '../lib';
import { useStomp } from '../providers';
import { useAuth } from './useAuth';
import {
  AnalyticsEvent,
  analyticsPage,
  analyticsTrack,
} from '../services/analytics';

const TOKEN_PARAM = 'token';
const GAME_ID_PARAM = 'gameId';
const DEALER_APPLET = 'DEALER_APPLET' as const;

type LoginFn = (params: {
  token: string;
  loginSource: typeof DEALER_APPLET;
}) => Promise<{ status: string; data: { token: string } }>;

interface UseAuthFlowParams {
  // localStorage key for this game's auth token
  appTokenKey: string;
  // if set, we check the gameId URL param matches before logging in
  validGameId?: string;
  // pass: (params) => loginMutation(params).unwrap()
  login: LoginFn;
}

// Handles the one-time token login flow shared across all games.
// Each game's AuthLayout just calls this and renders the result.
export function useAuthFlow({
  appTokenKey,
  validGameId,
  login,
}: UseAuthFlowParams) {
  const [error, setError] = useState<string | null>(null);
  const isLoginAttemptedRef = useRef(false);

  // Keep login in a ref so we don't have to add it to the effect deps.
  const loginRef = useRef(login);
  loginRef.current = login;

  const { reconnect } = useStomp();
  const { applyToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oneTimeToken = searchParams.get(TOKEN_PARAM);
  const gameId = searchParams.get(GAME_ID_PARAM);
  const authToken = searchParams.get('authtoken');

  const isInvalidGame = !!(validGameId && gameId && gameId !== validGameId);

  useEffect(() => {
    if (isInvalidGame) return;

    async function loginWithOneTimeToken() {
      if (!oneTimeToken || !gameId || isLoginAttemptedRef.current) return;
      isLoginAttemptedRef.current = true;
      const loginUniqueId = uuidv4();

      analyticsPage(AnalyticsEvent.LOGIN_STARTED, {
        gameId,
        uniqueId: loginUniqueId,
      });

      try {
        const result = await loginRef.current({
          token: oneTimeToken,
          loginSource: DEALER_APPLET,
        });

        if (result.status === 'SUCCESS') {
          localStorage.setItem(appTokenKey, result.data.token);
          applyToken(result.data.token);
          reconnect(result.data.token);
          analyticsTrack(AnalyticsEvent.LOGIN_SUCCESSFUL, {
            gameId,
            uniqueId: loginUniqueId,
          });
          navigate('/', { replace: true });
        } else {
          setError('Authentication failed. Please try again.');
          localStorage.removeItem(appTokenKey);
          analyticsTrack(AnalyticsEvent.LOGIN_FAILED, {
            gameId,
            uniqueId: loginUniqueId,
            reason: getApiErrorReason(result),
          });
        }
      } catch (err) {
        setError('Authentication error. Please try again later.');
        localStorage.removeItem(appTokenKey);
        analyticsTrack(AnalyticsEvent.LOGIN_FAILED, {
          gameId,
          uniqueId: loginUniqueId,
          reason: getApiErrorReason(err),
        });
      }
    }

    if (oneTimeToken && gameId) {
      loginWithOneTimeToken();
    } else if (authToken) {
      localStorage.setItem(appTokenKey, authToken);
      applyToken(authToken);
      reconnect(authToken);
      navigate('/', { replace: true });
    }
  }, [
    gameId,
    navigate,
    oneTimeToken,
    reconnect,
    authToken,
    appTokenKey,
    isInvalidGame,
    applyToken,
  ]);

  return { error, isInvalidGame };
}
